<script lang="ts">
  import { Citas, Consultorios, type Cita, type CitaEstado, type Profesional } from '../lib/api';
  import { session } from '../lib/auth.svelte';
  import { tenantCtx } from '../lib/tenant.svelte';
  import { infoClinica, urlAgendarPublico, type ClinicaPublicaInfo } from '../lib/citas-publico';
  import CitaAsistenteWidget from './CitaAsistenteWidget.svelte';

  let error = $state<string | null>(null);
  let info  = $state<string | null>(null);
  let cargando = $state(false);

  function fail(e: any) { error = e?.message ?? String(e); info = null; }
  function ok(m: string) { info = m; error = null; }

  function hoyISO() { return new Date().toISOString().slice(0, 10); }
  let dia = $state(hoyISO());

  let citas = $state<Cita[]>([]);
  let profesionales = $state<Profesional[]>([]);

  const ESTADOS: CitaEstado[] = ['PROGRAMADA', 'CONFIRMADA', 'ATENDIDA', 'CANCELADA', 'NO_ASISTIO'];
  const ESTADO_LABEL: Record<CitaEstado, string> = {
    PROGRAMADA: 'Programada', CONFIRMADA: 'Confirmada', ATENDIDA: 'Atendida',
    CANCELADA: 'Cancelada', NO_ASISTIO: 'No asistió',
  };

  async function cargar() {
    cargando = true; error = null;
    try {
      citas = await Citas.listar(dia);
    } catch (e) { fail(e); }
    finally { cargando = false; }
  }

  $effect(() => { dia; cargar(); });
  $effect(() => {
    void Consultorios.profesionales().then(p => { profesionales = p; }).catch(() => { profesionales = []; });
  });

  function cambiarDia(deltaDias: number) {
    const d = new Date(dia + 'T00:00:00');
    d.setDate(d.getDate() + deltaDias);
    dia = d.toISOString().slice(0, 10);
  }

  function hora(iso: string) {
    try { return new Date(iso).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  }

  // ── Nueva cita ──────────────────────────────────────────────────────────
  function citaVacia(): Cita {
    return {
      pacienteNombre: '', telefono: '', profesional: '', consultorio: '',
      motivo: '', fechaHora: `${dia}T09:00`, duracionMin: 30, estado: 'PROGRAMADA',
    };
  }
  let mostrarForm = $state(false);
  let nueva = $state<Cita>(citaVacia());

  function abrirForm() { nueva = citaVacia(); mostrarForm = true; }

  async function crear(e: SubmitEvent) {
    e.preventDefault();
    try {
      // El input datetime-local da "YYYY-MM-DDTHH:mm"; lo enviamos tal cual
      // (el backend lo interpreta en la zona del servidor / America/Guatemala).
      const payload: Cita = { ...nueva, fechaHora: new Date(nueva.fechaHora).toISOString() };
      await Citas.crear(payload);
      ok('Cita agendada.');
      mostrarForm = false;
      await cargar();
    } catch (ex) { fail(ex); }
  }

  async function cambiarEstado(c: Cita, estado: CitaEstado) {
    try { await Citas.cambiarEstado(c.id!, estado); await cargar(); }
    catch (ex) { fail(ex); }
  }

  async function recordar(c: Cita) {
    try {
      const r = await Citas.recordar(c.id!);
      if (r.enviado && r.simulado) ok('Recordatorio simulado (WhatsApp no configurado en este entorno).');
      else if (r.enviado) ok('Recordatorio enviado por WhatsApp.');
      else fail(r.error || 'No se pudo enviar el recordatorio.');
      await cargar();
    } catch (ex) { fail(ex); }
  }

  async function eliminar(c: Cita) {
    if (!confirm('¿Eliminar esta cita?')) return;
    try { await Citas.eliminar(c.id!); await cargar(); }
    catch (ex) { fail(ex); }
  }

  const linkPublico = $derived(
    session.value?.tenantId ? urlAgendarPublico(session.value.tenantId) : null,
  );

  /** Asistente de citas para pacientes: solo edicion clinica, no hospital. */
  const mostrarAsistentePaciente = $derived(
    !!linkPublico && !tenantCtx.isHospitalEdition,
  );

  let verAsistente = $state(true);
  let clinicaAsistente = $state<ClinicaPublicaInfo | null>(null);

  async function cargarClinicaAsistente() {
    const tid = session.value?.tenantId;
    if (!tid || tenantCtx.isHospitalEdition) {
      clinicaAsistente = null;
      return;
    }
    try {
      clinicaAsistente = await infoClinica(tid);
    } catch {
      clinicaAsistente = {
        tenantId: tid,
        nombre: tenantCtx.displayName,
        tipoOrganizacion: tenantCtx.orgTipo ?? undefined,
      };
    }
  }

  let asistenteKey = $state('');

  $effect(() => {
    if (!mostrarAsistentePaciente) {
      clinicaAsistente = null;
      asistenteKey = '';
      return;
    }
    const tid = session.value?.tenantId ?? '';
    const key = `${tid}|${tenantCtx.orgTipo ?? ''}`;
    if (asistenteKey === key && clinicaAsistente) return;
    asistenteKey = key;
    void cargarClinicaAsistente();
  });

  function copiarLinkPublico() {
    if (!linkPublico) return;
    void navigator.clipboard.writeText(linkPublico).then(() => ok('Enlace del asistente copiado.'));
  }
</script>

<div class="agenda">
  <header class="head">
    <div class="nav">
      <button onclick={() => cambiarDia(-1)} type="button" title="Día anterior">‹</button>
      <input type="date" bind:value={dia} />
      <button onclick={() => cambiarDia(1)} type="button" title="Día siguiente">›</button>
      <button class="hoy" onclick={() => dia = hoyISO()} type="button">Hoy</button>
    </div>
    <button class="primary" onclick={abrirForm} type="button">+ Nueva cita</button>
    {#if mostrarAsistentePaciente}
      <button class="link-pub" type="button" onclick={() => verAsistente = !verAsistente}>
        {verAsistente ? 'Ocultar asistente' : 'Mostrar asistente'}
      </button>
      <button class="link-pub" type="button" onclick={copiarLinkPublico} title={linkPublico!}>
        Copiar enlace
      </button>
      <a class="link-pub open" href={linkPublico!} target="_blank" rel="noopener noreferrer">
        Abrir en pestaña
      </a>
    {/if}
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  {#if mostrarAsistentePaciente && verAsistente && clinicaAsistente && session.value?.tenantId}
    <section class="card asistente-panel">
      <h3>Asistente para pacientes</h3>
      <p class="asist-lead">
        Agendar por texto o voz. Recordatorios automáticos ~24 h antes y a las 7:00 a.m. el día de la cita.
      </p>
      <div class="asist-inner">
        <CitaAsistenteWidget tenantId={session.value.tenantId} clinica={clinicaAsistente} />
      </div>
    </section>
  {/if}

  {#if mostrarForm}
    <form class="card form" onsubmit={crear}>
      <h3>Nueva cita</h3>
      <div class="grid">
        <label>Paciente / cliente *
          <input bind:value={nueva.pacienteNombre} required placeholder="Nombre completo" />
        </label>
        <label>WhatsApp / teléfono
          <input bind:value={nueva.telefono} type="tel" placeholder="+502 5555-1234" />
        </label>
        <label>Fecha y hora *
          <input bind:value={nueva.fechaHora} type="datetime-local" required />
        </label>
        <label>Duración (min)
          <input bind:value={nueva.duracionMin} type="number" min="5" step="5" />
        </label>
        <label>Profesional
          <select bind:value={nueva.profesional}>
            <option value="">— Seleccionar profesional —</option>
            {#each profesionales as p (p.id)}
              <option value={p.nombre}>{p.nombre}{p.colegiado ? ` · Col. ${p.colegiado}` : ''}</option>
            {/each}
            {#if nueva.profesional && !profesionales.some(p => p.nombre === nueva.profesional)}
              <option value={nueva.profesional}>{nueva.profesional}</option>
            {/if}
          </select>
          {#if profesionales.length === 0}
            <small class="muted">Registre profesionales en Administración → Profesionales.</small>
          {/if}
        </label>
        <label>Consultorio / sala
          <input bind:value={nueva.consultorio} placeholder="Consultorio 1" />
        </label>
        <label class="wide">Motivo
          <input bind:value={nueva.motivo} placeholder="Motivo de la cita" />
        </label>
      </div>
      <div class="acciones">
        <button type="button" class="ghost" onclick={() => mostrarForm = false}>Cancelar</button>
        <button type="submit" class="primary">Agendar</button>
      </div>
    </form>
  {/if}

  {#if cargando}
    <p class="muted">Cargando agenda…</p>
  {:else if citas.length === 0}
    <p class="muted">No hay citas para este día.</p>
  {:else}
    <ul class="lista">
      {#each citas as c (c.id)}
        <li class="cita estado-{c.estado}">
          <div class="hora">{hora(c.fechaHora)}<small>{c.duracionMin ?? 30}'</small></div>
          <div class="info">
            <strong>{c.pacienteNombre}</strong>
            {#if c.motivo}<span class="motivo">{c.motivo}</span>{/if}
            <span class="meta">
              {#if c.profesional}{c.profesional} · {/if}
              {#if c.consultorio}{c.consultorio} · {/if}
              {#if c.telefono}{c.telefono}{/if}
            </span>
            {#if c.recordatorioEnviado}<span class="recok">✓ recordatorio 24h</span>{/if}
            {#if c.recordatorioMatutinoEnviado}<span class="recok">✓ aviso 7am</span>{/if}
          </div>
          <div class="ctrl">
            <select value={c.estado} onchange={(e) => cambiarEstado(c, (e.currentTarget as HTMLSelectElement).value as CitaEstado)}>
              {#each ESTADOS as s}<option value={s}>{ESTADO_LABEL[s]}</option>{/each}
            </select>
            <button class="wa" type="button" onclick={() => recordar(c)} disabled={!c.telefono} title="Enviar recordatorio por WhatsApp">WhatsApp</button>
            <button class="del" type="button" onclick={() => eliminar(c)} title="Eliminar">🗑</button>
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .agenda { max-width: 920px; }
  .head { display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap; }
  .nav { display: flex; align-items: center; gap: .4rem; }
  .nav button { border: 1px solid #D6E7EA; background: #fff; border-radius: 8px; padding: .4rem .7rem; cursor: pointer; font: inherit; }
  .nav .hoy { color: #005F73; font-weight: 600; }
  .nav input[type=date] { padding: .4rem .6rem; border: 1px solid #D6E7EA; border-radius: 8px; font: inherit; }
  .primary { background: #00BFC7; color: #fff; border: 0; border-radius: 8px; padding: .55rem .9rem; font: inherit; font-weight: 600; cursor: pointer; }
  .primary:hover { background: #0091A0; }
  .link-pub { background: #fff; color: #005F73; border: 1px solid #00BFC7; border-radius: 8px; padding: .55rem .9rem; font: inherit; font-weight: 600; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center; }
  .link-pub:hover { background: #E6FAFB; }
  .ghost { background: #fff; border: 1px solid #D6E7EA; border-radius: 8px; padding: .55rem .9rem; cursor: pointer; font: inherit; }
  .err { color: #b00020; } .ok { color: #00796b; } .muted { color: #5B7480; }

  .card { background: #fff; border: 1px solid #D6E7EA; border-radius: 12px; padding: 1rem 1.25rem; margin-bottom: 1.25rem; }
  .asistente-panel h3 { margin: 0 0 0.35rem; color: #005F73; font-size: 1rem; }
  .asist-lead { margin: 0 0 0.75rem; color: #5B7480; font-size: 0.88rem; line-height: 1.45; }
  .asist-inner { max-width: 480px; margin: 0 auto; min-height: 420px; }
  .form h3 { margin: 0 0 .8rem; color: #005F73; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; }
  .grid .wide { grid-column: 1 / -1; }
  label { display: block; font-size: .8rem; color: #5B7480; font-weight: 500; }
  input, select { display: block; width: 100%; box-sizing: border-box; padding: .5rem .65rem; border: 1px solid #D6E7EA; border-radius: 8px; font: inherit; margin-top: .25rem; }
  .acciones { display: flex; justify-content: flex-end; gap: .6rem; margin-top: 1rem; }

  .lista { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: .5rem; }
  .cita { display: grid; grid-template-columns: 64px 1fr auto; gap: .9rem; align-items: center; background: #fff; border: 1px solid #D6E7EA; border-left: 4px solid #00BFC7; border-radius: 10px; padding: .65rem .9rem; }
  .cita.estado-CONFIRMADA { border-left-color: #2e7d32; }
  .cita.estado-ATENDIDA   { border-left-color: #5B7480; opacity: .8; }
  .cita.estado-CANCELADA  { border-left-color: #b00020; opacity: .65; }
  .cita.estado-NO_ASISTIO { border-left-color: #e65100; opacity: .7; }
  .hora { font-weight: 700; color: #005F73; text-align: center; }
  .hora small { display: block; color: #5B7480; font-weight: 400; }
  .info { display: flex; flex-direction: column; gap: .15rem; }
  .info .motivo { color: #0B2E36; font-size: .9rem; }
  .info .meta { color: #5B7480; font-size: .8rem; }
  .info .recok { color: #00796b; font-size: .75rem; }
  .ctrl { display: flex; align-items: center; gap: .4rem; }
  .ctrl select { width: auto; margin: 0; padding: .4rem .5rem; }
  .wa { background: #25D366; color: #fff; border: 0; border-radius: 8px; padding: .45rem .7rem; cursor: pointer; font: inherit; font-size: .82rem; font-weight: 600; }
  .wa:disabled { background: #b8e6c8; cursor: not-allowed; }
  .del { background: #fff; border: 1px solid #D6E7EA; border-radius: 8px; padding: .4rem .55rem; cursor: pointer; }
  @media (max-width: 620px) { .grid { grid-template-columns: 1fr; } .cita { grid-template-columns: 52px 1fr; } .ctrl { grid-column: 1 / -1; } }
</style>
